import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import { useMutation } from 'react-query';
import { Box, Card } from '@mui/material';

import { Alert, Button, TextInput, LoadingIndicator, When, ServicePage, SubTitle } from '../common';
import { signup } from '../../api';
import { CREATE, LOGIN } from '../../constants/routePaths';
import { yup, buildSubmitHandler, handleError } from '../../utils';
import PageContent from '../common/PageContent';
import OuterCardLogo from '../common/OuterCardLogo';
import CardText from '../common/CardText';
import Transition from '../common/Transition';
import { EMAIL_IN_USE } from '../../constants/apiResponseCodes';
import { REFERRAL_CODE_KEY } from '../../constants';
import { generalErrorLocation } from '../../constants/errorMessages';
import { getMessageOverride } from '../../utils/reactQueryHelpers';

/**
 * Setting up state for React router history usage
 * @param {Location} location - The location object from the React router
 * @returns {Object}
 */
const getSignupHistoryState = (location) => {
  const locationUrl = new URL(document.location.href);

  return [
    {
      key: 'returnUrl',
      params: ['return_url', 'redirect_url', 'store_url']
    },
    {
      key: 'emailAddress',
      params: ['email_address', 'emailaddress', 'emailAddress', 'email']
    },
    {
      key: 'returnUrlButtonText',
      params: ['return_button', 'returnbutton', 'returnButton']
    },
    {
      key: 'fromLoginForm',
      params: ['fromLoginForm']
    }
  ].reduce((acc, { key, params }) => {
    acc[key] = params.reduce((result, param) => (
      // Check the url query string first, then location reacts router state, or lastly set the default value
      locationUrl.searchParams.get(param) || location.state?.[key] || result
    ), '');

    return acc;
  }, {
    returnUrl: '',
    emailAddress: '',
    returnUrlButtonText: '',
    fromLoginForm: false
  });
};

const AccountSignup = () => {
  const history = useHistory();
  const location = useLocation();
  const state = getSignupHistoryState(location);
  const { emailAddress, fromLoginForm } = state;
  const referralCode = localStorage.getItem(REFERRAL_CODE_KEY);

  const { mutate } = useMutation(({ values }) => signup(values), {
    onSuccess: (data) => {
      history.push(CREATE, { ...state, emailAddress: data.emailAddress });
    },
    onError: (err, variables) => {
      // if user already exists, redirect to login page
      const hasEmailInUseError = err?.response?.data?.errors.some((error) => error.code === EMAIL_IN_USE);
      if (hasEmailInUseError) {
        const loginUrl = referralCode ? `${LOGIN}?${REFERRAL_CODE_KEY}=${referralCode}` : LOGIN;
        const message = err?.response?.data?.errors.find(
          (error) => error.code === EMAIL_IN_USE
        )?.message;

        // If user presses "Create a New Account" button from the login form, and email is already in use,
        // empty the email before redirecting to the create account page
        if (fromLoginForm) {
          history.push(CREATE, { ...state, emailAddress: '' });
        } else {
          history.push(loginUrl, {
            ...state,
            emailAddress: variables?.values?.emailAddress,
            signupError: getMessageOverride(generalErrorLocation, EMAIL_IN_USE, message),
          });
        }
      }
      handleError(err, variables);
    },
  });
  const { errors, values, handleSubmit, submitForm, handleChange, touched, isSubmitting } =
    useFormik({
      initialValues: { emailAddress },
      validationSchema: yup.createSchema({
        emailAddress: yup.emailAddress,
      }),
      validateOnBlur: false,
      validateOnChange: false,
      onSubmit: buildSubmitHandler(mutate),
    });

  useEffect(() => {
    if (emailAddress !== '') {
      submitForm();
    }
  }, [emailAddress, submitForm]);

  return (
    <ServicePage>
      <PageContent maxWidth="xs">
        <OuterCardLogo />
        <Card>
          <When it={!referralCode}>
            <SubTitle cardTitle sx={{ fontSize: '1.25rem' }} data-testid="standard-title">
              Get Started with Showit
            </SubTitle>
            <CardText data-testid="standard-description">
              Start your free 14-day trial and experience the creative freedom enjoyed by thousands
              of other creatives like you.
            </CardText>
          </When>
          <When it={!!referralCode}>
            <SubTitle cardTitle sx={{ fontSize: '1.25rem' }} data-testid="referral-title">
              Congratulations! <br />
              You&rsquo;re Snagging a Month Free
            </SubTitle>
            <CardText data-testid="referral-description">
              Start your account today to save this special subscription offer and to start your
              14-day free trial.
            </CardText>
          </When>
          <When it={isSubmitting}>
            <LoadingIndicator />
          </When>
          <When
            it={errors.general}
            render={() => (
              <Transition>
                <Alert severity="error" variant="outlined" noBorder>
                  {errors.general}
                </Alert>
              </Transition>
            )}
          />
          <When it={!isSubmitting}>
            <form onSubmit={handleSubmit}>
              <TextInput
                type="text"
                variant="outlined"
                label="Your email"
                name="emailAddress"
                value={values.emailAddress}
                onChange={handleChange}
                error={touched.emailAddress && !!errors.emailAddress}
                helperText={touched.emailAddress && errors.emailAddress}
                data-testid="email-input"
              />
              <Box mb={3}>
                <Button fullWidth type="submit" variant="contained" data-testid="signup-button">
                  Get Started
                </Button>
              </Box>
            </form>
          </When>
        </Card>
      </PageContent>
    </ServicePage>
  );
};

export default AccountSignup;
